import { MouseEvent, RefObject, useRef } from "react";
import CustomButton from "../CustomButton";
import { ResolvedActionMenuItem } from "./actionMenu.interface";
import ActionMenuTooltip from "./ActionMenuTooltip";
import ActionMenuKindIndicator from "./ActionMenuKindIndicator";
import { actionMenuKindIcons } from "./actionMenuKindConfig";

interface ActionMenuButtonProps {
  item: ResolvedActionMenuItem;
  registerTrigger: (id: string, element: HTMLElement | null) => void;
  menuRef: RefObject<HTMLElement>;
  tooltipOpen: boolean;
  onTooltipOpen: () => void;
  onTooltipClose: () => void;
  disabled?: boolean;
  onSelect: (
    item: ResolvedActionMenuItem,
    event: MouseEvent<HTMLElement>,
  ) => void;
}

function ActionMenuButton({
  item,
  registerTrigger,
  menuRef,
  tooltipOpen,
  onTooltipOpen,
  onTooltipClose,
  disabled,
  onSelect,
}: ActionMenuButtonProps) {
  const itemRef = useRef<HTMLSpanElement | null>(null);
  const capturedButton = item.capturedButton;
  const canNavigate =
    !item.withConfirmationModal && !capturedButton?.withDropdown && !disabled;
  const isNeutralColor =
    !item.color || item.color === "default" || item.color === "inherit";
  const isWarningColor = item.color === "warning";
  const leadingIcon =
    item.kind && item.icon === actionMenuKindIcons[item.kind]
      ? "DOCUMENTS"
      : item.icon;

  return (
    <ActionMenuTooltip
      title={item.tooltip}
      menuRef={menuRef}
      itemRef={itemRef}
      open={tooltipOpen}
      onOpen={onTooltipOpen}
      onClose={onTooltipClose}>
      <span
        ref={itemRef}
        className="inline-flex"
        tabIndex={disabled && item.tooltip ? 0 : undefined}
        aria-disabled={disabled || undefined}
        onFocus={onTooltipOpen}
        onBlur={onTooltipClose}>
        <CustomButton
          buttonRef={(element) => registerTrigger(item.id, element)}
          icon={leadingIcon}
          startIcon={capturedButton?.startIcon}
          endIcon={capturedButton?.endIcon}
          variant={item.variant ?? "outlined"}
          aria-label={item.inlineIconOnly ? item.label : undefined}
          color={
            item.color && item.color !== "default" ? item.color : "inherit"
          }
          sx={
            item.variant === "action"
              ? undefined
              : {
                  ...(isNeutralColor
                    ? { color: "text.primary", borderColor: "divider" }
                    : {}),
                  ...(isWarningColor
                    ? { color: "#C2410C", borderColor: "#EA580C" }
                    : {}),
                  fontWeight: 500,
                  lineHeight: 1.5,
                  borderRadius: "6px",
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  "&:hover, &.Mui-focusVisible": {
                    ...(item.variant === "contained"
                      ? {}
                      : {
                          backgroundColor: isWarningColor
                            ? "#FFF7ED"
                            : "#F1F5F9",
                        }),
                    ...(isNeutralColor
                      ? { borderColor: "text.secondary" }
                      : {}),
                    ...(isWarningColor ? { borderColor: "#C2410C" } : {}),
                  },
                }
          }
          navigate={canNavigate ? item.navigate : undefined}
          href={
            canNavigate && !item.navigate ? capturedButton?.href : undefined
          }
          withDefaultAnchor={capturedButton?.withDefaultAnchor}
          target={capturedButton?.target}
          download={capturedButton?.download}
          rel={capturedButton?.rel}
          disabled={disabled}
          loading={item.loading}
          onClick={(event) => {
            if (disabled) {
              event.preventDefault();
              return;
            }
            onSelect(item, event);
          }}>
          {item.inlineIconOnly ? null : (
            <>
              {item.label}
              <ActionMenuKindIndicator kind={item.kind} />
            </>
          )}
        </CustomButton>
      </span>
    </ActionMenuTooltip>
  );
}

export default ActionMenuButton;
